// client/src/pages/ArchivePage.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useTheme } from '../hooks/useTheme';
import {
  createArchivedCard,
  getArchivedCards,
  updateArchivedCard,
  deleteArchivedCard,
  inviteToVelox
} from '../api/archive';
import { pickReadableTextColors } from '../utils/contrast';
import { runOfflineOcr } from '../utils/offlineOcr';
import ArchiveDuplicateEmailModal from '../components/ArchiveDuplicateEmailModal';
import {
  fileToObjectUrl,
  revokeObjectUrl
} from '../utils/businessCardImagePipeline';
import { EMPTY_ARCHIVE_FIELDS, scanImageToDraft } from '../utils/archiveScan';
import homeStyles from '../components/home/HomeHub.module.css';
import { HomeSidebar, HomeTopBar } from '../components/home/HomeHubParts';
import NotificationsDrawer from '../components/home/NotificationsDrawer';
import useHubNotifications from '../hooks/useHubNotifications';
import '../styles/ArchivePage.css';

function isInviteOnCooldown(card) {
  if (!card?.invitedAt || !card?.inviteCount) return false;
  const invitedDate = new Date(card.invitedAt);
  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
  return invitedDate > twentyFourHoursAgo;
}

function getCardThemeStyle(card) {
  const bg = card?.theme?.primaryBackground || card?.primaryColor || null;
  if (!bg) return {};
  const fallback = pickReadableTextColors(bg);
  return {
    backgroundColor: bg,
    '--archive-card-text': card.theme?.primaryText || fallback.primaryText,
    '--archive-card-subtext': card.theme?.secondaryText || fallback.secondaryText
  };
}

function getInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function makeLocalId() {
  return `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

const ArchivePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const notifications = useHubNotifications();
  const { showSuccess, showError, showInfo } = useToast();
  useTheme(); // Initialize theme for this page
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const [cards, setCards] = useState([]);
  const [archiveStats, setArchiveStats] = useState({ archived: 0, converted: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ done: 0, total: 0 });
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [showBatchReview, setShowBatchReview] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [formFields, setFormFields] = useState({ ...EMPTY_ARCHIVE_FIELDS });
  const [imageUrl, setImageUrl] = useState(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [theme, setTheme] = useState(null);
  const [ocrLines, setOcrLines] = useState([]);
  const [fieldConfidence, setFieldConfidence] = useState({});
  const [activeField, setActiveField] = useState('name');
  const [offlineOcrBlob, setOfflineOcrBlob] = useState(null);
  const [isOfflineOcrRunning, setIsOfflineOcrRunning] = useState(false);
  const [duplicateEmailCard, setDuplicateEmailCard] = useState(null);
  const [showDuplicateEmailModal, setShowDuplicateEmailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [page] = useState(1);
  const [invitingId, setInvitingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState([]);
  const [batchDrafts, setBatchDrafts] = useState([]);
  const [batchIndex, setBatchIndex] = useState(0);
  const [isSavingBatch, setIsSavingBatch] = useState(false);
  const searchInputRef = useRef(null);

  const clearPendingPhotos = useCallback(() => {
    setPendingPhotos((prev) => {
      prev.forEach((p) => {
        if (p.previewUrl) revokeObjectUrl(p.previewUrl);
      });
      return [];
    });
  }, []);

  const clearBatchDrafts = useCallback(() => {
    setBatchDrafts((prev) => {
      prev.forEach((d) => {
        if (d.previewUrl) revokeObjectUrl(d.previewUrl);
      });
      return [];
    });
    setBatchIndex(0);
  }, []);

  // Load archived cards
  const loadCards = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getArchivedCards({ page, limit: 20 });
      setCards(response.cards || []);
      setArchiveStats({
        archived: response.stats?.archived ?? response.pagination?.total ?? (response.cards || []).length,
        converted: response.stats?.converted ?? 0
      });
    } catch (error) {
      console.error('Error loading archived cards:', error);
      showError('Failed to load archived cards');
    } finally {
      setIsLoading(false);
    }
  }, [page, showError]);

  useEffect(() => {
    loadCards();
  }, [loadCards]);

  // Enable scrolling on app-content for archive page
  useEffect(() => {
    const appContent = document.querySelector('.app-content');
    if (appContent) {
      appContent.classList.add('archive-page-active');
    }
    return () => {
      if (appContent) {
        appContent.classList.remove('archive-page-active');
      }
    };
  }, []);

  const addFilesToPending = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const next = [];

    for (const file of files) {
      if (!allowedTypes.includes(file.type)) {
        showError(`Skipped “${file.name || 'file'}”: use JPEG, PNG, or WebP.`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        showError(`Skipped “${file.name || 'file'}”: larger than 10MB.`);
        continue;
      }
      try {
        const previewUrl = await fileToObjectUrl(file);
        next.push({
          id: makeLocalId(),
          file,
          previewUrl,
          name: file.name || 'Photo'
        });
      } catch (e) {
        console.error('Error preparing photo:', e);
        showError('Could not add one of the photos. Please try again.');
      }
    }

    if (next.length) {
      setPendingPhotos((prev) => [...prev, ...next]);
    }
  }, [showError]);

  // Handle file selection (camera or gallery) — queue photos, no crop
  const handleFileSelect = async (event) => {
    const files = event.target.files;
    await addFilesToPending(files);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  const removePendingPhoto = (id) => {
    setPendingPhotos((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.previewUrl) revokeObjectUrl(target.previewUrl);
      return prev.filter((p) => p.id !== id);
    });
  };

  const processPendingPhotos = async () => {
    if (!pendingPhotos.length || isUploading) return;

    const photos = [...pendingPhotos];
    setShowCaptureModal(false);
    setIsUploading(true);
    setUploadProgress({ done: 0, total: photos.length });

    const drafts = [];
    for (let i = 0; i < photos.length; i += 1) {
      const photo = photos[i];
      setUploadProgress({ done: i, total: photos.length });
      try {
        const scanned = await scanImageToDraft(photo.file);
        drafts.push({
          id: photo.id,
          previewUrl: photo.previewUrl,
          ...scanned,
          status: 'ready',
          error: null
        });
      } catch (e) {
        console.error('Scan failed for photo:', e);
        drafts.push({
          id: photo.id,
          previewUrl: photo.previewUrl,
          fields: { ...EMPTY_ARCHIVE_FIELDS },
          theme: null,
          primaryColor: null,
          imageUrl: null,
          s3Key: null,
          fieldConfidence: {},
          ocrLines: [],
          offlineOcrBlob: photo.file,
          status: 'error',
          error: 'Could not read this card. Enter details manually or remove it.'
        });
      }
    }

    // pendingPhotos ownership of preview URLs moves to drafts
    setPendingPhotos([]);
    setBatchDrafts(drafts);
    setBatchIndex(0);
    setUploadProgress({ done: photos.length, total: photos.length });
    setIsUploading(false);
    setShowBatchReview(true);
    showInfo(
      photos.length === 1
        ? 'Scanned. Review the details and save.'
        : `Scanned ${photos.length} cards. Review each, then save.`
    );
  };

  // Handle update card
  const handleUpdateCard = async () => {
    if (!selectedCard) return;

    try {
      setIsSaving(true);
      await updateArchivedCard(selectedCard._id, {
        fields: formFields,
        theme: theme || undefined,
        fieldConfidence: fieldConfidence || undefined
      });
      showSuccess('Archived card updated successfully');
      setShowEditModal(false);
      resetForm();
      loadCards();
    } catch (error) {
      console.error('Error updating card:', error);
      
      // Handle network errors
      if (!error.response) {
        showError('Unable to connect to the server. Please check your internet connection and try again.');
      } else {
        // Handle specific error cases with user-friendly messages
        const errorMessage = error.response?.data?.error || '';
        const statusCode = error.response?.status;
        
        if (statusCode === 500) {
          showError('An unexpected error occurred. Please try again in a moment.');
        } else if (errorMessage && (errorMessage.includes('required') || errorMessage.includes('invalid'))) {
          // Use user-friendly backend messages for validation errors
          showError(errorMessage);
        } else {
          showError('Failed to update archived card. Please try again.');
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete card
  const handleDeleteCard = async () => {
    if (!selectedCard) return;

    try {
      await deleteArchivedCard(selectedCard._id);
      showSuccess('Archived card deleted successfully');
      setShowDeleteConfirm(false);
      setSelectedCard(null);
      loadCards();
    } catch (error) {
      console.error('Error deleting card:', error);
      showError('Failed to delete archived card');
    }
  };

  // Handle invite
  const handleInvite = async (card) => {
    if (!card?._id || invitingId) return;
    try {
      setInvitingId(card._id);
      await inviteToVelox(card._id);
      showSuccess('Invite sent successfully!');
      loadCards(); // Refresh to update invite metadata
    } catch (error) {
      console.error('Error sending invite:', error);
      // Handle network errors
      if (!error.response) {
        showError('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error.response?.status === 429) {
        showError('Please wait 24 hours before sending another invite.');
      } else {
        // Handle specific error cases with user-friendly messages
        const errorMessage = error.response?.data?.error || '';
        const statusCode = error.response?.status;
        
        if (statusCode === 500) {
          showError('An unexpected error occurred. Please try again in a moment.');
        } else if (errorMessage && (errorMessage.includes('required') || errorMessage.includes('invalid') || errorMessage.includes('email'))) {
          // Use user-friendly backend messages for validation errors
          showError(errorMessage);
        } else {
          showError('Failed to send invite. Please try again.');
        }
      }
    } finally {
      setInvitingId(null);
    }
  };

  // Handle edit
  const handleEdit = (card) => {
    if (imagePreviewUrl) revokeObjectUrl(imagePreviewUrl);
    setImagePreviewUrl(null);
    setSelectedCard(card);
    setFormFields({
      name: card.fields?.name || '',
      email: card.fields?.email || '',
      phone: card.fields?.phone || '',
      company: card.fields?.company || '',
      jobTitle: card.fields?.jobTitle || '',
      website: card.fields?.website || '',
      address: card.fields?.address || '',
      notes: card.fields?.notes || ''
    });
    setImageUrl(card.imageUrl);
    setTheme(card.theme || (card.primaryColor ? { primaryBackground: card.primaryColor, ...pickReadableTextColors(card.primaryColor) } : null));
    setFieldConfidence(card.fieldConfidence || {});
    setOcrLines([]);
    setShowEditModal(true);
  };

  // Reset form (existing-card edit modal)
  const resetForm = () => {
    setFormFields({ ...EMPTY_ARCHIVE_FIELDS });
    setImageUrl(null);
    if (imagePreviewUrl) revokeObjectUrl(imagePreviewUrl);
    setImagePreviewUrl(null);
    setTheme(null);
    setOcrLines([]);
    setFieldConfidence({});
    setOfflineOcrBlob(null);
    setIsOfflineOcrRunning(false);
    setDuplicateEmailCard(null);
    setShowDuplicateEmailModal(false);
    setSelectedCard(null);
  };

  const updateBatchDraftField = (id, key, value) => {
    setBatchDrafts((prev) =>
      prev.map((d) => (d.id === id ? { ...d, fields: { ...d.fields, [key]: value } } : d))
    );
  };

  const removeBatchDraft = (id) => {
    setBatchDrafts((prev) => {
      const target = prev.find((d) => d.id === id);
      if (target?.previewUrl) revokeObjectUrl(target.previewUrl);
      const next = prev.filter((d) => d.id !== id);
      if (!next.length) {
        setShowBatchReview(false);
        setBatchIndex(0);
        return [];
      }
      setBatchIndex((idx) => Math.min(idx, next.length - 1));
      return next;
    });
  };

  const closeBatchReview = () => {
    clearBatchDrafts();
    setShowBatchReview(false);
  };

  const saveBatchDrafts = async () => {
    if (!batchDrafts.length || isSavingBatch) return;
    setIsSavingBatch(true);
    let saved = 0;
    let failed = 0;
    const remaining = [];

    for (const draft of batchDrafts) {
      try {
        await createArchivedCard({
          imageUrl: draft.imageUrl || null,
          fields: draft.fields,
          s3Key: draft.s3Key || null,
          primaryColor: draft.primaryColor || null,
          theme: draft.theme || null,
          fieldConfidence: draft.fieldConfidence || null,
          ocrRaw: { timestamp: Date.now(), engine: draft.theme ? 'scan-business-card' : 'legacy' }
        });
        if (draft.previewUrl) revokeObjectUrl(draft.previewUrl);
        saved += 1;
      } catch (error) {
        console.error('Error saving batch draft:', error);
        failed += 1;
        remaining.push({
          ...draft,
          status: 'error',
          error:
            error.response?.data?.error === 'duplicate_email'
              ? 'This email is already in your archive.'
              : 'Could not save. Check details and try again.'
        });
      }
    }

    setIsSavingBatch(false);

    if (!remaining.length) {
      setBatchDrafts([]);
      setShowBatchReview(false);
      setBatchIndex(0);
      showSuccess(saved === 1 ? 'Card saved to archive.' : `${saved} cards saved to archive.`);
      loadCards();
      return;
    }

    setBatchDrafts(remaining);
    setBatchIndex(0);
    if (saved > 0) {
      showInfo(`Saved ${saved}. ${failed} need attention.`);
      loadCards();
    } else {
      showError('Could not save these cards. Please review and try again.');
    }
  };

  // Escape closes the top-most archive modal
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (showDuplicateEmailModal) {
        setShowDuplicateEmailModal(false);
        setDuplicateEmailCard(null);
        return;
      }
      if (showDeleteConfirm) {
        setShowDeleteConfirm(false);
        return;
      }
      if (showInfoModal) {
        setShowInfoModal(false);
        return;
      }
      if (showBatchReview) {
        closeBatchReview();
        return;
      }
      if (showEditModal) {
        setShowEditModal(false);
        resetForm();
        return;
      }
      if (showCaptureModal) {
        clearPendingPhotos();
        setShowCaptureModal(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showDuplicateEmailModal,
    showDeleteConfirm,
    showInfoModal,
    showBatchReview,
    showEditModal,
    showCaptureModal,
    imagePreviewUrl
  ]);

  // "/" focuses search when not typing in a field (desktop power-user affordance).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = e.target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target?.isContentEditable) return;
      if (
        showCaptureModal ||
        showEditModal ||
        showBatchReview ||
        showDeleteConfirm ||
        showInfoModal ||
        showDuplicateEmailModal
      ) {
        return;
      }
      e.preventDefault();
      searchInputRef.current?.focus();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    showCaptureModal,
    showEditModal,
    showBatchReview,
    showDeleteConfirm,
    showInfoModal,
    showDuplicateEmailModal
  ]);

  // Filter cards by search query
  const trimmedQuery = searchQuery.trim();
  const filteredCards = cards.filter(card => {
    if (!trimmedQuery) return true;
    const query = trimmedQuery.toLowerCase();
    const searchableText = [
      card.fields?.name,
      card.fields?.email,
      card.fields?.company,
      card.fields?.jobTitle,
      card.fields?.phone
    ].filter(Boolean).join(' ').toLowerCase();
    return searchableText.includes(query);
  });

  const activeBatchDraft = batchDrafts[batchIndex] || null;


  const getConfidenceMeta = (fieldKey) => {
    const v = fieldConfidence?.[fieldKey];
    if (typeof v !== 'number') return null;
    if (v >= 0.85) return { label: 'High', level: 'high' };
    if (v >= 0.65) return { label: 'Med', level: 'med' };
    return { label: 'Low', level: 'low' };
  };

  const handleRunOfflineOcr = async () => {
    if (!offlineOcrBlob || isOfflineOcrRunning) return;
    setIsOfflineOcrRunning(true);
    try {
      const result = await runOfflineOcr(offlineOcrBlob, { lang: 'eng' });
      const f = result?.fields || {};
      setFormFields((prev) => ({
        ...prev,
        name: f.name || prev.name,
        email: f.email || prev.email,
        phone: f.phone || prev.phone,
        company: f.company || prev.company,
        jobTitle: f.jobTitle || prev.jobTitle,
        website: f.website || prev.website,
        address: f.address || prev.address,
        notes: prev.notes || f.notes || ''
      }));
      showSuccess('Offline OCR complete. Please review and save.');
    } catch (e) {
      console.error('Offline OCR failed:', e);
      showError('Offline OCR failed. Please enter details manually.');
    } finally {
      setIsOfflineOcrRunning(false);
    }
  };

  return (
    <div className={homeStyles.hubRoot}>
      <HomeSidebar
        open={isNavOpen}
        onClose={() => setIsNavOpen(false)}
        activePath={location.pathname}
        onNavigate={(to) => {
          setIsNavOpen(false);
          navigate(to);
        }}
      />
      <NotificationsDrawer
        open={notifications.open}
        onClose={notifications.close}
        {...notifications.drawerProps}
      />
      <div className={homeStyles.hubMain}>
        <HomeTopBar
          title="Archive"
          onMenu={() => setIsNavOpen(true)}
          notificationsBadge={notifications.badge}
          onNotifications={notifications.toggle}
          onInfo={() => setShowInfoModal(true)}
        />
        <div className={`${homeStyles.hubContent} archive-hub-content`}>
    <div className="archive-page">
      <header className="archive-page-head">
        <div className="archive-page-head-text">
          <h1 className="archive-page-title">Archive</h1>
          <p className="archive-page-meta">
            Scan paper business cards to upload a digital copy. Invite them to Velox for live contacts if you choose.
          </p>
          {!isLoading ? (
            <p className="archive-stats" aria-label="Archive totals">
              <span className="archive-stats-item">
                <strong>{archiveStats.archived}</strong> archived
              </span>
              <span className="archive-stats-sep" aria-hidden="true">·</span>
              <span className="archive-stats-item">
                <strong>{archiveStats.converted}</strong> converted to live
              </span>
            </p>
          ) : null}
        </div>
      </header>

      <div className="archive-toolbar" role="search">
        <div className="archive-search">
          <span className="archive-search-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
            </svg>
          </span>
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search by name, company, email…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="archive-search-input"
            aria-label="Search archived cards"
          />
          {searchQuery ? (
            <button
              type="button"
              className="archive-search-clear"
              onClick={() => {
                setSearchQuery('');
                searchInputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          ) : (
            <span className="archive-search-kbd" aria-hidden="true">/</span>
          )}
        </div>
        <button
          type="button"
          className="archive-scan-btn"
          onClick={() => setShowCaptureModal(true)}
          disabled={isUploading}
          aria-label={isUploading ? 'Processing card' : 'Scan card'}
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          <span className="archive-scan-btn-label">{isUploading ? 'Processing…' : 'Scan card'}</span>
        </button>
      </div>

      {isUploading ? (
        <div className="archive-processing" role="status" aria-live="polite">
          <div className="archive-spinner" />
          <div>
            <p className="archive-processing-title">
              Reading cards… {uploadProgress.total > 0 ? `${Math.min(uploadProgress.done + 1, uploadProgress.total)} of ${uploadProgress.total}` : ''}
            </p>
            <p className="archive-processing-hint">Extracting details from your photos</p>
          </div>
        </div>
      ) : null}

      {!isLoading && trimmedQuery && filteredCards.length > 0 ? (
        <p className="archive-results-meta" aria-live="polite">
          Showing <strong>{filteredCards.length}</strong> of {cards.length}
        </p>
      ) : null}

      <div className="archive-cards-container">
        {isLoading ? (
          <div className="archive-cards-list" aria-busy="true" aria-label="Loading archived cards">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="archive-card archive-card--skeleton" style={{ animationDelay: `${i * 50}ms` }}>
                <div className="archive-card-main">
                  <div className="archive-card-image archive-skeleton-block" />
                  <div className="archive-card-info">
                    <div className="archive-skeleton-line archive-skeleton-line--lg" />
                    <div className="archive-skeleton-line" />
                    <div className="archive-skeleton-line archive-skeleton-line--sm" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : filteredCards.length === 0 ? (
          <div className="archive-empty" role="status">
            <span className="archive-empty-icon" aria-hidden="true">
              {trimmedQuery ? (
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M20 20l-3.5-3.5" strokeLinecap="round" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <rect x="3" y="6" width="18" height="12" rx="2" />
                  <path d="M3 10h18" />
                </svg>
              )}
            </span>
            <p className="archive-empty-title">
              {trimmedQuery ? 'No matching cards' : 'No archived cards yet'}
            </p>
            <p className="archive-empty-hint">
              {trimmedQuery
                ? `Nothing matches “${trimmedQuery}”. Try another name, company, or email.`
                : 'Photograph a business card to save details and invite them to Velox.'}
            </p>
            {trimmedQuery ? (
              <button
                type="button"
                className="archive-empty-action"
                onClick={() => {
                  setSearchQuery('');
                  searchInputRef.current?.focus();
                }}
              >
                Clear search
              </button>
            ) : (
              <button
                type="button"
                className="archive-empty-action archive-empty-action--primary"
                onClick={() => setShowCaptureModal(true)}
                disabled={isUploading}
              >
                Scan a card
              </button>
            )}
          </div>
        ) : (
          <div className="archive-cards-list">
            {filteredCards.map((card, index) => {
              const inviteCooling = isInviteOnCooldown(card);
              const hasInvite = Boolean(card.invitedAt && card.inviteCount > 0);
              const displayName = card.fields?.name || 'Unnamed card';
              const isInviting = invitingId === card._id;
              return (
                <article
                  key={card._id}
                  className={`archive-card${card.theme?.primaryBackground || card.primaryColor ? ' archive-card--themed' : ''}`}
                  style={{ ...getCardThemeStyle(card), animationDelay: `${Math.min(index, 12) * 35}ms` }}
                >
                  <button
                    type="button"
                    className="archive-card-main"
                    onClick={() => handleEdit(card)}
                    aria-label={`Edit ${displayName}`}
                  >
                    <div className="archive-card-image">
                      {card.imageUrl ? (
                        <img src={card.imageUrl} alt="" />
                      ) : (
                        <div className="archive-card-no-image" aria-hidden="true">
                          <span>{getInitials(displayName)}</span>
                        </div>
                      )}
                    </div>
                    <div className="archive-card-info">
                      <div className="archive-card-info-top">
                        <h3 className="archive-card-name">{displayName}</h3>
                        {hasInvite ? (
                          <span className={`archive-card-status${inviteCooling ? ' archive-card-status--sent' : ''}`}>
                            {inviteCooling ? 'Invited' : 'Was invited'}
                          </span>
                        ) : (
                          <span className="archive-card-status archive-card-status--static">Static</span>
                        )}
                      </div>
                      {(card.fields?.company || card.fields?.jobTitle) && (
                        <p className="archive-card-company">
                          {[card.fields?.jobTitle, card.fields?.company].filter(Boolean).join(' · ')}
                        </p>
                      )}
                      <div className="archive-card-contacts">
                        {card.fields?.email && (
                          <p className="archive-card-email">{card.fields.email}</p>
                        )}
                        {card.fields?.phone && (
                          <p className="archive-card-phone">{card.fields.phone}</p>
                        )}
                      </div>
                    </div>
                    <span className="archive-card-chevron" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6" />
                      </svg>
                    </span>
                  </button>
                  <div className="archive-card-actions">
                    {card.fields?.email && (
                      <button
                        type="button"
                        className="archive-card-invite"
                        onClick={() => handleInvite(card)}
                        disabled={inviteCooling || isInviting}
                        title={inviteCooling ? 'Invite already sent. Wait 24 hours.' : 'Invite to Velox'}
                      >
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                          <polyline points="22,6 12,13 2,6" />
                        </svg>
                        {isInviting ? 'Sending…' : inviteCooling ? 'Invited' : 'Invite'}
                      </button>
                    )}
                    <button
                      type="button"
                      className="archive-card-edit"
                      onClick={() => handleEdit(card)}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                      Edit
                    </button>
                    <button
                      type="button"
                      className="archive-card-delete"
                      onClick={() => {
                        setSelectedCard(card);
                        setShowDeleteConfirm(true);
                      }}
                      aria-label={`Delete ${displayName}`}
                    >
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                      </svg>
                      <span className="archive-card-delete-label">Delete</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      {/* Capture Modal — queue multiple photos, no crop */}
      {showCaptureModal && (
        <div
          className="archive-modal-overlay"
          onClick={() => {
            clearPendingPhotos();
            setShowCaptureModal(false);
          }}
        >
          <div
            className="archive-modal archive-capture-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-capture-title"
          >
            <div className="archive-modal-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="1.75">
                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                <circle cx="12" cy="13" r="4" />
              </svg>
            </div>
            <h3 id="archive-capture-title">Add business cards</h3>
            <p>Take or upload photos of paper cards to upload a digital version.</p>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {pendingPhotos.length > 0 ? (
              <div className="archive-pending-strip" aria-label={`${pendingPhotos.length} photos ready`}>
                {pendingPhotos.map((photo) => (
                  <div key={photo.id} className="archive-pending-thumb">
                    <img src={photo.previewUrl} alt="" />
                    <button
                      type="button"
                      className="archive-pending-remove"
                      onClick={() => removePendingPhoto(photo.id)}
                      aria-label="Remove photo"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            ) : null}

            <div className="archive-modal-buttons archive-modal-buttons--stack">
              <button
                type="button"
                className="archive-modal-button-primary"
                onClick={() => cameraInputRef.current?.click()}
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                  <circle cx="12" cy="13" r="4" />
                </svg>
                Take photo
              </button>
              <button
                type="button"
                className="archive-modal-button-secondary"
                onClick={() => fileInputRef.current?.click()}
              >
                Choose from gallery
              </button>
              {pendingPhotos.length > 0 ? (
                <button
                  type="button"
                  className="archive-modal-button-primary"
                  onClick={processPendingPhotos}
                >
                  Review {pendingPhotos.length} {pendingPhotos.length === 1 ? 'card' : 'cards'}
                </button>
              ) : null}
              <button
                type="button"
                className="archive-modal-button-cancel"
                onClick={() => {
                  clearPendingPhotos();
                  setShowCaptureModal(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch review after multi-scan */}
      {showBatchReview && activeBatchDraft && (
        <div className="archive-modal-overlay" onClick={closeBatchReview}>
          <div
            className="archive-modal archive-batch-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="archive-batch-title"
          >
            <div className="archive-batch-header">
              <div>
                <h3 id="archive-batch-title">Review cards</h3>
                <p className="archive-modal-lead archive-batch-lead">
                  Card {batchIndex + 1} of {batchDrafts.length}. Edit details, then save all.
                </p>
              </div>
              <button
                type="button"
                className="archive-info-close-button"
                onClick={closeBatchReview}
                aria-label="Close review"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="archive-batch-tabs" role="tablist" aria-label="Cards in this batch">
              {batchDrafts.map((draft, i) => (
                <button
                  key={draft.id}
                  type="button"
                  role="tab"
                  aria-selected={i === batchIndex}
                  className={`archive-batch-tab${i === batchIndex ? ' archive-batch-tab--active' : ''}${draft.status === 'error' ? ' archive-batch-tab--error' : ''}`}
                  onClick={() => setBatchIndex(i)}
                >
                  <img src={draft.previewUrl} alt="" />
                  <span>{draft.fields?.name || `Card ${i + 1}`}</span>
                </button>
              ))}
            </div>

            {activeBatchDraft.error ? (
              <p className="archive-batch-error" role="alert">{activeBatchDraft.error}</p>
            ) : null}

            <div className="archive-batch-body">
              <div className="archive-edit-image">
                <img src={activeBatchDraft.previewUrl || activeBatchDraft.imageUrl} alt="Business card" />
              </div>
              {activeBatchDraft.theme?.primaryBackground ? (
                <div
                  className="archive-scan-preview-card"
                  style={{
                    backgroundColor: activeBatchDraft.theme.primaryBackground,
                    color: activeBatchDraft.theme.primaryText
                  }}
                >
                  <div className="archive-scan-preview-name">{activeBatchDraft.fields.name || 'Name'}</div>
                  <div className="archive-scan-preview-company" style={{ color: activeBatchDraft.theme.secondaryText }}>
                    {[activeBatchDraft.fields.company, activeBatchDraft.fields.jobTitle].filter(Boolean).join(' • ') || 'Company • Title'}
                  </div>
                </div>
              ) : null}

              <div className="archive-edit-form">
                {[
                  ['name', 'Name', 'text', 'Full name'],
                  ['email', 'Email', 'email', 'email@example.com'],
                  ['phone', 'Phone', 'tel', '+1 234 567 8900'],
                  ['company', 'Company', 'text', 'Company name'],
                  ['jobTitle', 'Job title', 'text', 'Job title'],
                  ['website', 'Website', 'url', 'https://example.com'],
                  ['address', 'Address', 'text', 'Address']
                ].map(([key, label, type, placeholder]) => (
                  <div className="archive-form-group" key={key}>
                    <label htmlFor={`batch-${activeBatchDraft.id}-${key}`}>{label}</label>
                    <input
                      id={`batch-${activeBatchDraft.id}-${key}`}
                      type={type}
                      value={activeBatchDraft.fields[key] || ''}
                      onChange={(e) => updateBatchDraftField(activeBatchDraft.id, key, e.target.value)}
                      placeholder={placeholder}
                    />
                  </div>
                ))}
                <div className="archive-form-group">
                  <label htmlFor={`batch-${activeBatchDraft.id}-notes`}>Notes</label>
                  <textarea
                    id={`batch-${activeBatchDraft.id}-notes`}
                    value={activeBatchDraft.fields.notes || ''}
                    onChange={(e) => updateBatchDraftField(activeBatchDraft.id, 'notes', e.target.value)}
                    placeholder="Additional notes…"
                    rows="3"
                  />
                </div>
              </div>
            </div>

            <div className="archive-batch-nav">
              <button
                type="button"
                className="archive-modal-button-secondary"
                disabled={batchIndex <= 0}
                onClick={() => setBatchIndex((i) => Math.max(0, i - 1))}
              >
                Previous
              </button>
              <button
                type="button"
                className="archive-modal-button-cancel"
                onClick={() => {
                  if (batchDrafts.length <= 1) {
                    closeBatchReview();
                    return;
                  }
                  removeBatchDraft(activeBatchDraft.id);
                }}
              >
                Remove card
              </button>
              <button
                type="button"
                className="archive-modal-button-secondary"
                disabled={batchIndex >= batchDrafts.length - 1}
                onClick={() => setBatchIndex((i) => Math.min(batchDrafts.length - 1, i + 1))}
              >
                Next
              </button>
            </div>

            <div className="archive-modal-buttons">
              <button
                type="button"
                className="archive-modal-button-cancel"
                onClick={closeBatchReview}
                disabled={isSavingBatch}
              >
                Discard all
              </button>
              <button
                type="button"
                className="archive-modal-button-primary"
                onClick={saveBatchDrafts}
                disabled={isSavingBatch || !batchDrafts.length}
              >
                {isSavingBatch
                  ? 'Saving…'
                  : batchDrafts.length === 1
                    ? 'Save card'
                    : `Save all ${batchDrafts.length}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit existing archived card */}
      {showEditModal && (
        <div className="archive-modal-overlay" onClick={() => { setShowEditModal(false); resetForm(); }}>
          <div className="archive-modal archive-edit-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit archived card</h3>
            <p className="archive-modal-lead">Update the saved details for this static card.</p>
            {(imagePreviewUrl || imageUrl || selectedCard?.imageUrl) && (
              <div className="archive-edit-image">
                <img src={imagePreviewUrl || imageUrl || selectedCard?.imageUrl} alt="Business card" />
              </div>
            )}
            {theme?.primaryBackground && (
              <div
                className="archive-scan-preview-card"
                style={{
                  backgroundColor: theme.primaryBackground,
                  color: theme.primaryText
                }}
              >
                <div className="archive-scan-preview-name">{formFields.name || 'Name'}</div>
                <div className="archive-scan-preview-company" style={{ color: theme.secondaryText }}>
                  {[formFields.company, formFields.jobTitle].filter(Boolean).join(' • ') || 'Company • Title'}
                </div>
                <div className="archive-scan-preview-meta" style={{ color: theme.secondaryText }}>
                  {[formFields.email, formFields.phone, formFields.website].filter(Boolean).join(' • ') || 'Email • Phone • Website'}
                </div>
              </div>
            )}
            <div className="archive-edit-form">
              <div className="archive-form-group">
                <label>
                  Name
                  {getConfidenceMeta('name') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('name').level}`}>
                      {getConfidenceMeta('name').label}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={formFields.name}
                  onChange={(e) => setFormFields({ ...formFields, name: e.target.value })}
                  placeholder="Full name"
                  onFocus={() => setActiveField('name')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Email
                  {getConfidenceMeta('email') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('email').level}`}>
                      {getConfidenceMeta('email').label}
                    </span>
                  )}
                </label>
                <input
                  type="email"
                  value={formFields.email}
                  onChange={(e) => setFormFields({ ...formFields, email: e.target.value })}
                  placeholder="email@example.com"
                  onFocus={() => setActiveField('email')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Phone
                  {getConfidenceMeta('phone') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('phone').level}`}>
                      {getConfidenceMeta('phone').label}
                    </span>
                  )}
                </label>
                <input
                  type="tel"
                  value={formFields.phone}
                  onChange={(e) => setFormFields({ ...formFields, phone: e.target.value })}
                  placeholder="+1 234 567 8900"
                  onFocus={() => setActiveField('phone')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Company
                  {getConfidenceMeta('company') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('company').level}`}>
                      {getConfidenceMeta('company').label}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={formFields.company}
                  onChange={(e) => setFormFields({ ...formFields, company: e.target.value })}
                  placeholder="Company name"
                  onFocus={() => setActiveField('company')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Job Title
                  {getConfidenceMeta('jobTitle') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('jobTitle').level}`}>
                      {getConfidenceMeta('jobTitle').label}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={formFields.jobTitle}
                  onChange={(e) => setFormFields({ ...formFields, jobTitle: e.target.value })}
                  placeholder="Job title"
                  onFocus={() => setActiveField('jobTitle')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Website
                  {getConfidenceMeta('website') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('website').level}`}>
                      {getConfidenceMeta('website').label}
                    </span>
                  )}
                </label>
                <input
                  type="url"
                  value={formFields.website}
                  onChange={(e) => setFormFields({ ...formFields, website: e.target.value })}
                  placeholder="https://example.com"
                  onFocus={() => setActiveField('website')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Address
                  {getConfidenceMeta('address') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('address').level}`}>
                      {getConfidenceMeta('address').label}
                    </span>
                  )}
                </label>
                <input
                  type="text"
                  value={formFields.address}
                  onChange={(e) => setFormFields({ ...formFields, address: e.target.value })}
                  placeholder="Address"
                  onFocus={() => setActiveField('address')}
                />
              </div>
              <div className="archive-form-group">
                <label>
                  Notes
                  {getConfidenceMeta('notes') && (
                    <span className={`archive-confidence-badge ${getConfidenceMeta('notes').level}`}>
                      {getConfidenceMeta('notes').label}
                    </span>
                  )}
                </label>
                <textarea
                  value={formFields.notes}
                  onChange={(e) => setFormFields({ ...formFields, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows="3"
                  onFocus={() => setActiveField('notes')}
                />
              </div>
            </div>
            {(ocrLines?.length || 0) > 0 && !selectedCard && (
              <div className="archive-scan-lines">
                <div className="archive-scan-lines-header">
                  <div>
                    <strong>Extracted lines</strong>
                    <span className="archive-scan-lines-hint"> Tap a line to fill “{activeField}”</span>
                  </div>
                  <button
                    type="button"
                    className="archive-scan-swap"
                    onClick={() => setFormFields((f) => ({ ...f, name: f.company, company: f.name }))}
                  >
                    Swap name/company
                  </button>
                </div>
                <div className="archive-scan-lines-list">
                  {ocrLines.map((l) => (
                    <button
                      type="button"
                      key={l.id}
                      className="archive-scan-line"
                      onClick={() => {
                        const text = (l.text || '').trim();
                        if (!text) return;
                        setFormFields((prev) => ({
                          ...prev,
                          [activeField]: text
                        }));
                      }}
                    >
                      <span className="archive-scan-line-text">{l.text}</span>
                      {typeof l.confidence === 'number' && (
                        <span className="archive-scan-line-conf">{Math.round(l.confidence)}%</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="archive-modal-buttons">
              {!selectedCard && offlineOcrBlob && (
                <button
                  className="archive-modal-button-secondary"
                  type="button"
                  onClick={handleRunOfflineOcr}
                  disabled={isOfflineOcrRunning}
                >
                  {isOfflineOcrRunning ? 'Running offline OCR…' : 'Try offline OCR (slow)'}
                </button>
              )}
              <button
                type="button"
                className="archive-modal-button-primary"
                onClick={handleUpdateCard}
                disabled={isSaving || !selectedCard}
              >
                {isSaving ? 'Saving…' : 'Update'}
              </button>
              <button
                className="archive-modal-button-cancel"
                onClick={() => { setShowEditModal(false); resetForm(); }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ArchiveDuplicateEmailModal
        isOpen={showDuplicateEmailModal}
        existingCard={duplicateEmailCard}
        newFields={formFields}
        newTheme={theme}
        fieldConfidence={fieldConfidence}
        onCancel={() => {
          setShowDuplicateEmailModal(false);
          setDuplicateEmailCard(null);
        }}
        onViewExisting={(card) => {
          if (!card?._id) return;
          setShowDuplicateEmailModal(false);
          setDuplicateEmailCard(null);
          // Open the existing card in edit mode (readable theme should come from persisted data).
          handleEdit(card);
        }}
        onConfirmUpdate={async ({ fieldsPatch, applyTheme }) => {
          try {
            const targetId = duplicateEmailCard?._id;
            if (!targetId) return;
            await updateArchivedCard(targetId, {
              fields: fieldsPatch,
              theme: applyTheme ? (theme || null) : undefined,
              fieldConfidence: applyTheme ? (fieldConfidence || null) : undefined
            });
            showSuccess('Existing archived card updated.');
            setShowDuplicateEmailModal(false);
            setDuplicateEmailCard(null);
            setShowEditModal(false);
            resetForm();
            loadCards();
          } catch (e) {
            console.error('Error updating existing archived card:', e);
            showError('Failed to update the existing archived card. Please try again.');
          }
        }}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="archive-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="archive-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete archived card?</h3>
            <p>
              {selectedCard?.fields?.name
                ? `Remove “${selectedCard.fields.name}” from your archive? This can’t be undone.`
                : 'Remove this card from your archive? This can’t be undone.'}
            </p>
            <div className="archive-modal-buttons">
              <button
                className="archive-modal-button-danger"
                onClick={handleDeleteCard}
              >
                Delete
              </button>
              <button
                className="archive-modal-button-cancel"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Information Modal */}
      {showInfoModal && (
        <div className="archive-modal-overlay" onClick={() => setShowInfoModal(false)}>
          <div className="archive-modal archive-info-modal" onClick={(e) => e.stopPropagation()}>
            <div className="archive-info-modal-header">
              <div className="archive-info-modal-icon">
                <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
              </div>
              <h3>Archive Cards</h3>
              <button 
                className="archive-info-close-button"
                onClick={() => setShowInfoModal(false)}
                aria-label="Close"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="archive-info-modal-content">
              <p>
                <strong>Archive Cards:</strong> These are manually added, static cards not linked to live Velox profiles. 
                Invite owners to join Velox for a live, auto-updating card.
              </p>
              <p>
                These archived cards are manually added from photos of physical business cards. They are <strong>static</strong> and 
                do not automatically update like live Velox contacts. To get a live, auto-updating contact card, use the 
                <strong>"Invite to Velox"</strong> button on any archived card to invite the owner to join Velox Network. 
                Once they sign up, their archived card will automatically be replaced with a live contact.
              </p>
            </div>
            <div className="archive-modal-buttons">
              <button
                className="archive-modal-button-primary"
                onClick={() => setShowInfoModal(false)}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
        </div>
      </div>
    </div>
  );
};

export default ArchivePage;

